"use client";

import { useEffect, useState } from "react";
import {
  LIVE_FEED_INTERVAL,
  LIVE_FEED_TRANSITION,
} from "@/components/section/landingPageSections/liveFeedItems";

/**
 * Drives the fade-out/swap/fade-in cycle for the hero's "live activity"
 * notification widget: every `LIVE_FEED_INTERVAL` ms it hides the current
 * entry, and `LIVE_FEED_TRANSITION` ms later swaps to the next index
 * (wrapping via `itemCount`) and shows it again. Skips the interval
 * entirely for visitors who prefer reduced motion — callers render
 * `LIVE_FEED_ITEMS[index]` and fade on `visible`, so a reduced-motion
 * visitor just sees the first entry, static. Split out of `HeroNew.tsx` so
 * this timer logic stays independently testable.
 */
export default function useLiveFeedRotation(itemCount: number) {
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (
      typeof window !== "undefined" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      return;
    }
    let swapTimer: ReturnType<typeof setTimeout> | undefined;
    const interval = setInterval(() => {
      setVisible(false);
      swapTimer = setTimeout(() => {
        setIndex((i) => (i + 1) % itemCount);
        setVisible(true);
      }, LIVE_FEED_TRANSITION);
    }, LIVE_FEED_INTERVAL);
    return () => {
      clearInterval(interval);
      clearTimeout(swapTimer);
    };
  }, [itemCount]);

  return { index, visible };
}
